import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Badge,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Bolt as BoltIcon,
  Menu as MenuIcon,
  Business as BusinessIcon,
  Favorite as FavoriteIcon,
  Work as WorkIcon,
  MeetingRoom as MeetingRoomIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  Add as AddIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  QrCode as QrCodeIcon,
} from "@mui/icons-material";
import { AppSidebar } from "@/components/AppSidebar";
import { useState, useEffect } from "react";
import { ROOM_TYPE_LABELS, RoomType, Room } from "@/types/profile";
import { RoomQrDialog } from "@/components/RoomQrDialog";
import { getRooms, deleteRoom, RoomResponse } from "@/services/roomService";
import { useToast } from "@/contexts/ToastContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { useMyProfile } from "@/contexts/ProfileContext";
import { toRoomType } from "@/utils/roomType";

const getRoomIcon = (type: RoomType) => {
  switch (type) {
    case "business": return <BusinessIcon />;
    case "dating":   return <FavoriteIcon />;
    case "job":      return <WorkIcon />;
  }
};

const getRoomColor = (type: RoomType) => {
  switch (type) {
    case "business": return { bg: "#e0e7ff", color: "#6366f1" };
    case "dating":   return { bg: "#fce7f3", color: "#ec4899" };
    case "job":      return { bg: "#d1fae5", color: "#059669" };
  }
};

const formatDate = (date?: Date) => {
  if (!date) return "未設定";
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric", month: "long", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).format(date);
};

const mapRoomResponseToRoom = (response: RoomResponse): Room => {
  return {
    id: response.roomId,
    name: response.roomName,
    type: toRoomType(response.categoryType),
    description: response.description,
    canEdit: Boolean(response.canEdit),
    eventDate: response.eventDatetime ? new Date(response.eventDatetime) : undefined,
    expiresAt: response.expirationDate ? new Date(response.expirationDate) : undefined,
    maxParticipants: response.maxParticipants,
    createdAt: response.createdAt ? new Date(response.createdAt) : new Date(),
    participants: [], // 参加者詳細は別途取得（現在はカウントのみ使用）
  };
};

export const HomePage = () => {
  const { user, isAdmin } = useAuth();
  const { totalUnread } = useNotifications();
  const { showSuccess, showError } = useToast();
  const { hasAnyProfile } = useMyProfile();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [qrRoom, setQrRoom] = useState<Room | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Room | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [roomParticipantCounts, setRoomParticipantCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoading(true);
        // 管理者は全ルーム、ユーザーは参加中のルームのみ取得
        const userId = isAdmin ? undefined : user?.userId;
        const roomResponses = await getRooms(userId);
        const mappedRooms = roomResponses.map(mapRoomResponseToRoom);
        // 参加者モードでは期限切れのルームを表示しない。
        // 開いても入室できず、一覧に残っていても操作できないため。
        // 管理者は期限の延長や削除のために引き続き表示する。
        const now = new Date();
        const visibleRooms = isAdmin
          ? mappedRooms
          : mappedRooms.filter((r) => !r.expiresAt || r.expiresAt >= now);
        setRooms(visibleRooms);
        
        // 参加者数をマッピング
        const counts: Record<string, number> = {};
        roomResponses.forEach(r => {
          counts[r.roomId] = r.participantCount;
        });
        setRoomParticipantCounts(counts);
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
  }, [isAdmin, user]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRoom(deleteTarget.id);
      // 一覧から即座に取り除く（再取得を待たずに画面へ反映する）
      setRooms((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
      showSuccess("ルームを削除しました");
    } catch (error) {
      console.error("Failed to delete room:", error);
      const code = (error as { code?: string }).code;
      showError(
        code === "functions/permission-denied"
          ? "このルームを削除する権限がありません。"
          : "ルームの削除に失敗しました。",
      );
    } finally {
      setDeleting(false);
    }
  };

  const totalParticipants = Object.values(roomParticipantCounts).reduce((sum, count) => sum + count, 0);

  // ── ルームカード ──────────────────────────────────────
  const renderRoomCard = (room: Room) => {
    const colors = getRoomColor(room.type);
    return (
      <Grid item xs={12} sm={6} md={4} key={room.id}>
        <Card
          sx={{
            height: "100%", display: "flex", flexDirection: "column",
            transition: "all 0.2s ease-in-out",
            "&:hover": { transform: isMobile ? "none" : "translateY(-4px)", boxShadow: 4 },
          }}
        >
          <CardContent sx={{ flex: 1, p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 44, height: 44, borderRadius: 2,
                  bgcolor: colors.bg, color: colors.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {getRoomIcon(room.type)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Chip
                  label={ROOM_TYPE_LABELS[room.type]}
                  size="small"
                  sx={{ bgcolor: colors.bg, color: colors.color, fontSize: "0.7rem", height: 22 }}
                />
              </Box>
            </Box>

            <Typography variant="h6" fontWeight={600} gutterBottom>
              {room.name}
            </Typography>

            {room.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {room.description}
              </Typography>
            )}

            {/* 管理者はイベント詳細、ユーザーは参加者数のみ */}
            {isAdmin ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CalendarIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(room.eventDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <PeopleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    {roomParticipantCounts[room.id] || 0}人参加中
                    {room.maxParticipants && ` / 上限 ${room.maxParticipants}人`}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                参加者: {roomParticipantCounts[room.id] || 0}人
              </Typography>
            )}
          </CardContent>

          <CardActions
            sx={{
              p: { xs: 2, sm: 2.5 }, pt: 0,
              flexDirection: "column", gap: 1, alignItems: "stretch",
            }}
          >
            {/* 作成者のみ: QRコード生成ボタン */}
            {room.canEdit && (
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<QrCodeIcon />}
                onClick={() => setQrRoom(room)}
                sx={{
                  borderColor: colors.color, color: colors.color,
                  "&:hover": { borderColor: colors.color, bgcolor: colors.bg },
                }}
              >
                入室用QR作成
              </Button>
            )}
            {/* 入室ボタン（共通） */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={<MeetingRoomIcon />}
                onClick={() => navigate(`/room/${room.id}`)}
                sx={{
                  bgcolor: colors.color,
                  "&:hover": { bgcolor: colors.color, filter: "brightness(0.9)" },
                }}
              >
                入室
              </Button>
              {/* 作成者のみ: 編集・削除アイコン */}
              {room.canEdit && (
                <>
                  <IconButton size="small" title="編集" onClick={() => navigate(`/admin/room/edit/${room.id}`)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" title="削除" onClick={() => setDeleteTarget(room)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  // ── レンダリング ──────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
            <Box
              sx={{
                width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 1.5,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <BoltIcon sx={{ color: "white", fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
              Comyuless
            </Typography>
            {isAdmin ? (
              <Chip
                icon={<AdminIcon sx={{ fontSize: "0.9rem !important" }} />}
                label="管理者画面"
                size="small"
                color="warning"
                variant="filled"
              />
            ) : user && !isMobile && (
              <Chip label="参加者モード" size="small" color="info" variant="outlined" />
            )}
          </Box>

          {user && (
            <IconButton onClick={() => setSidebarOpen(true)} color="inherit">
              <Badge badgeContent={totalUnread} color="error" max={99}>
                <MenuIcon />
              </Badge>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} showRoleSwitcher />

      {qrRoom && (
        <RoomQrDialog room={qrRoom} open={Boolean(qrRoom)} onClose={() => setQrRoom(null)} />
      )}

      {/* 削除確認モーダル */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteIcon color="error" />
          ルームの削除
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            この操作は取り消せません。
          </Alert>
          <Typography variant="body2">
            以下のルームを削除してもよいですか？
          </Typography>
          <Box
            sx={{
              mt: 1.5, px: 2, py: 1.5,
              bgcolor: "grey.50", borderRadius: 2,
              border: 1, borderColor: "divider",
            }}
          >
            <Typography fontWeight={700}>{deleteTarget?.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {deleteTarget ? ROOM_TYPE_LABELS[deleteTarget.type] : ""}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexDirection: "column" }}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            startIcon={<DeleteIcon />}
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {deleting ? "削除中..." : "削除する"}
          </Button>
          <Button
            variant="text"
            fullWidth
            color="inherit"
            onClick={() => setDeleteTarget(null)}
            sx={{ borderRadius: 2, color: "text.secondary" }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      <Box
        component="main"
        sx={{ flexGrow: 1, py: { xs: 2, sm: 4 }, bgcolor: isAdmin ? "grey.50" : undefined }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>

          {/* 管理者: ダッシュボードヘッダー + 統計 */}
          {isAdmin ? (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800}>
                  管理者ダッシュボード
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  ルームの作成・管理を行えます
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider", bgcolor: "background.paper" }}>
                    <Typography variant="h4" fontWeight={700} color="primary">{rooms.length}</Typography>
                    <Typography variant="body2" color="text.secondary">総ルーム数</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider", bgcolor: "background.paper" }}>
                    <Typography variant="h4" fontWeight={700} color="success.main">{totalParticipants}</Typography>
                    <Typography variant="body2" color="text.secondary">総参加者数</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: 1, borderColor: "divider", bgcolor: "background.paper" }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      fullWidth
                      size="large"
                      onClick={() => navigate("/admin/room/create")}
                      sx={{ height: "100%", minHeight: 56 }}
                    >
                      ルームを作成
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            </>
          ) : (
            /* ユーザー: ヒーローセクション */
            <Box sx={{ textAlign: "center", py: { xs: 3, sm: 5 } }}>
              <Typography
                variant={isMobile ? "h4" : "h2"}
                gutterBottom
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                Comyulessへようこそ
              </Typography>
              <Typography
                variant={isMobile ? "body1" : "h6"}
                color="text.secondary"
                sx={{ maxWidth: 600, mx: "auto", mb: { xs: 3, sm: 4 }, fontWeight: 400, px: { xs: 1, sm: 0 } }}
              >
                スマートな名刺交換で、ビジネス・婚活・転職をもっと効率的に。
              </Typography>
              {/* 登録済みの場合は「編集」に切り替える。
                  登録済みなのに「登録」ボタンを押すと、新規入力を求められて
                  未登録のように見えてしまうため。 */}
              <Button
                variant="contained"
                size={isMobile ? "medium" : "large"}
                startIcon={hasAnyProfile() ? <EditIcon /> : <PersonAddIcon />}
                onClick={() => navigate(hasAnyProfile() ? "/profile/edit" : "/profile/register")}
              >
                {hasAnyProfile() ? "プロフィールを編集" : "プロフィールを登録"}
              </Button>
            </Box>
          )}

          {/* ルーム一覧（共通） */}
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} sx={{ mb: 2 }}>
            {isAdmin ? "ルーム一覧" : "あなたが参加中のイベント"}
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {rooms.map(renderRoomCard)}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ py: { xs: 2, sm: 3 }, bgcolor: "background.paper", borderTop: 1, borderColor: "divider" }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            &copy; 2024 Comyuless. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
